import { useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { useResource } from '../hooks';
import { AuthCardSkeleton, ErrorNotice, Field, PasswordInput, ThemeToggle } from '../components/ui';
import { Input } from '../components/primitives';

/** AcceptInvitationPage handles an emailed invitation link
 * (/accept-invitation?token=...). It looks the token up first (without
 * spending it) to show who is inviting whom to what, then renders either a
 * "create your account" form or a "sign in to accept" prompt depending on
 * whether an account already exists for that email — see
 * service.AcceptInvitation's two paths. */
export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const lookup = useResource(() => api.getInvitation(token), [token]);

  if (!token) {
    return (
      <AuthShell>
        <ErrorNotice error={new Error('This invitation link is missing its token.')} />
      </AuthShell>
    );
  }

  if (lookup.loading) {
    return (
      <AuthShell>
        <AuthCardSkeleton />
      </AuthShell>
    );
  }

  if (lookup.error || !lookup.data) {
    const isGone = lookup.error instanceof ApiError && lookup.error.status === 410;
    return (
      <AuthShell>
        <h1>Invitation not available</h1>
        <p className="muted">
          {isGone
            ? 'This invitation has already been used, was revoked, or has expired.'
            : (lookup.error?.message ?? 'This invitation could not be found.')}
        </p>
        <Link className="button button--secondary" to="/login">
          Go to sign in
        </Link>
      </AuthShell>
    );
  }

  const invitation = lookup.data;

  return (
    <AuthShell>
      <h1>Join {invitation.organization_name}</h1>
      <p className="muted">
        <strong>{invitation.role_name}</strong> · {invitation.email}
      </p>
      {invitation.account_exists ? (
        <SignInThenAccept token={token} email={invitation.email} onAccepted={() => navigate('/app', { replace: true })} />
      ) : (
        <CreateAccountAndAccept token={token} onAccepted={() => navigate('/app', { replace: true })} />
      )}
    </AuthShell>
  );
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-shell">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <Link to="/" className="auth-card__brand">
          <BrandMark size={26} />
          Commerce Ops
        </Link>
        {children}
      </div>
    </div>
  );
}

function CreateAccountAndAccept({ token, onAccepted }: { token: string; onAccepted: () => void }) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.acceptInvitation(token, { name: name.trim(), password });
      onAccepted();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Field label="Your name">
        <Input required autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="Password" hint="At least 10 characters.">
        <PasswordInput
          required
          minLength={10}
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <ErrorNotice error={error} />
      <button className="button" type="submit" disabled={submitting || !name.trim() || password.length < 10}>
        {submitting ? 'Joining' : 'Create account and join'}
      </button>
    </form>
  );
}

function SignInThenAccept({ token, email, onAccepted }: { token: string; email: string; onAccepted: () => void }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.login({ email, password });
      await api.acceptInvitation(token, {});
      onAccepted();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Field label="Password">
        <PasswordInput
          required
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <ErrorNotice error={error} />
      <button className="button" type="submit" disabled={submitting || !password}>
        {submitting ? 'Joining' : 'Sign in and join'}
      </button>
    </form>
  );
}
